export type TemplateError = {
  message: string;
  start: number;
};

type ValidateOptions = {
  template: string;
  validVariables: readonly string[];
  listVariables: Record<string, readonly string[]>;
};

type BlockEntry = {
  subType: string;
  content: string;
  start: number;
  endOfOpenTag: number;
};

const BLOCK_REGEX = /^#(each|if|else|empty|select)\s*([A-Za-z_0-9\s=!<>]*)$/;
const IF_REGEX = /^([A-Za-z_]+)\s*(==|!=|<|>|<=|>=)?\s*(.+)?$/;
const CLOSING_BLOCKS = ['/each', '/if', '/select'] as const;
const EXPR_REGEX = /\{\{(.*?)\}\}/g;

function isClosingBlock(name: string): boolean {
  return (CLOSING_BLOCKS as readonly string[]).includes(name);
}

function closingMatchesOpen(closing: string, openSubType: string): boolean {
  if (closing === '/each') return openSubType === 'each' || openSubType === 'empty';
  if (closing === '/if') return openSubType === 'if' || openSubType === 'else';
  if (closing === '/select') return openSubType === 'select' || openSubType === 'empty';
  return false;
}

function stripOption(varName: string): string {
  const colonIndex = varName.indexOf(':');
  return colonIndex >= 0 ? varName.substring(0, colonIndex) : varName;
}

function currentListVar(blockStack: readonly BlockEntry[]): string | undefined {
  for (let i = blockStack.length - 1; i >= 0; i--) {
    const { subType } = blockStack[i];
    if (subType === 'each' || subType === 'select' || subType === 'empty') {
      return blockStack[i].content.split(' ')[0];
    }
  }
  return undefined;
}

function isVariableValidInContext(
  variable: string,
  validVariables: readonly string[],
  listVariables: Record<string, readonly string[]>,
  blockStack: readonly BlockEntry[],
): boolean {
  const listVar = currentListVar(blockStack);
  if (listVar) {
    const children = listVariables[listVar];
    if (!children) return true;
    return children.includes(variable);
  }
  return validVariables.includes(variable);
}

function hasContentBetween(template: string, from: number, to: number): boolean {
  const between = template.substring(from, to).trim();
  return between.length > 0;
}

/**
 * The list variable whose row supplies the variables at `cursor`, or undefined
 * at the top level.
 *
 * Inside an #each or #select the row is the whole render context, so only that
 * list's own variables resolve — the same rule isVariableValidInContext applies
 * when validating. The autocomplete reads this so it offers what would actually
 * render rather than the action's own variables, and the stack transitions here
 * mirror validateTemplate's exactly so the two cannot disagree about where a
 * block begins and ends.
 */
export function listContextAt(template: string, cursor: number): string | undefined {
  const before = template.slice(0, cursor);
  const blockStack: BlockEntry[] = [];

  const regex = new RegExp(EXPR_REGEX.source, 'g');
  let match: RegExpExecArray | null;

  while ((match = regex.exec(before)) !== null) {
    const inner = match[1].trim();
    const start = match.index;
    const endOfTag = start + match[0].length;

    const blockMatch = BLOCK_REGEX.exec(inner);

    if (blockMatch) {
      const subType = blockMatch[1];
      const content = blockMatch[2].trim();

      // A malformed #else leaves the stack untouched, as it does in
      // validateTemplate, so both read the same context out of broken markup.
      if (subType === 'else') {
        const parent = blockStack[blockStack.length - 1];
        if (!parent || parent.subType !== 'if') continue;
      }

      if (subType === 'else' || subType === 'empty') {
        blockStack.pop();
      }

      blockStack.push({ subType, content, start, endOfOpenTag: endOfTag });
    } else if (isClosingBlock(inner)) {
      blockStack.pop();
    }
  }

  return currentListVar(blockStack);
}

export type ContextVariablesOptions = {
  template: string;
  cursor: number;
  listVariables: Record<string, readonly string[]>;
  actionVariables: readonly string[];
  /** Used only when no action is selected, so there is nothing narrower to offer. */
  validVariables?: readonly string[];
};

/**
 * The variables that resolve at `cursor` — what any picker should be offering
 * there. Inside a loop that is the row's own set; outside it, the action's.
 *
 * An unrecognised list name falls back rather than returning nothing, matching
 * isVariableValidInContext, which accepts anything on a list it does not know.
 */
export function variablesInContext(options: ContextVariablesOptions): readonly string[] {
  const { template, cursor, listVariables, actionVariables, validVariables } = options;

  const listContext = listContextAt(template, cursor);
  if (listContext) {
    const rowVariables = listVariables[listContext];
    if (rowVariables) return rowVariables;
  }

  return actionVariables.length > 0 ? actionVariables : (validVariables ?? []);
}

export function validateTemplate(options: ValidateOptions): { errors: TemplateError[] } {
  const { template, validVariables, listVariables } = options;
  const errors: TemplateError[] = [];
  const blockStack: BlockEntry[] = [];

  const regex = new RegExp(EXPR_REGEX.source, 'g');
  let match: RegExpExecArray | null;

  while ((match = regex.exec(template)) !== null) {
    const fullMatch = match[0];
    const inner = match[1].trim();
    const start = match.index;
    const endOfTag = start + fullMatch.length;

    const blockMatch = BLOCK_REGEX.exec(inner);

    if (blockMatch) {
      const subType = blockMatch[1];
      const content = blockMatch[2].trim();

      if (subType === 'else' || subType === 'empty') {
        if (subType === 'else') {
          const parent = blockStack[blockStack.length - 1];
          if (!parent || parent.subType !== 'if') {
            errors.push({ message: `#else block should be a child of an if block`, start });
            continue;
          }
        }
        if (blockStack.length > 0) {
          blockStack.pop();
        }
      }

      if (subType === 'if') {
        const ifMatch = IF_REGEX.exec(content);
        if (ifMatch) {
          const variable = ifMatch[1];
          const operator = ifMatch[2] ?? '';
          const value = ifMatch[3]?.trim() ?? '';

          if (!validVariables.includes(variable)) {
            errors.push({ message: `Invalid variable: ${variable}`, start });
          }
          if (operator && !value) {
            errors.push({
              message: `if statement with an operator must have an operand on both sides, ${variable}`,
              start,
            });
          }
        }
      }

      if (subType === 'each' || subType === 'select') {
        const listName = content.split(' ')[0];
        if (!listVariables[listName]) {
          errors.push({
            message: `block #${subType} ${listName} is not a valid list variable`,
            start,
          });
        }
      }

      if (blockStack.length > 0) {
        blockStack[blockStack.length - 1] = {
          ...blockStack[blockStack.length - 1],
        };
      }

      blockStack.push({ subType, content, start, endOfOpenTag: endOfTag });
    } else if (isClosingBlock(inner)) {
      if (blockStack.length === 0) {
        errors.push({ message: `Unexpected closing block without matching open block`, start });
      } else {
        const lastBlock = blockStack[blockStack.length - 1];

        if (!hasContentBetween(template, lastBlock.endOfOpenTag, start)) {
          errors.push({
            message: `block #${lastBlock.subType} ${lastBlock.content} should not be empty`,
            start: lastBlock.start,
          });
        }

        if (!closingMatchesOpen(inner, lastBlock.subType)) {
          errors.push({ message: `Mismatched closing block: ${inner}`, start });
        }

        blockStack.pop();
      }
    } else {
      const varName = stripOption(inner);
      if (!isVariableValidInContext(varName, validVariables, listVariables, blockStack)) {
        errors.push({ message: `Invalid variable: ${varName}`, start });
      }
    }
  }

  for (let i = blockStack.length - 1; i >= 0; i--) {
    const block = blockStack[i];
    errors.push({
      message: `Unclosed block #${block.subType} ${block.content}`,
      start: block.start,
    });
  }

  return { errors };
}
